/**
 * Pure, I/O-free module: takes a parsed pricing.json object, returns the
 * normalised list of rate cells + meta. Used by both pricing-source.ts (golden
 * snapshot) and seed.ts (DB seeding) so location_id is identical everywhere.
 */
import { BANDS } from '../engine/index.js';
import type { Band } from '../engine/index.js';
import type { GridServiceId } from './service-schema.js';

// ---------------------------------------------------------------------------
// Raw pricing.json shape (mirrored from pricing-source.ts)
// ---------------------------------------------------------------------------
export type PricingRow = {
  name: string;
  p1_4: number | null;
  p4_8: number | null;
  p8_12: number | null;
  p12_16: number | null;
};

export type PricingJson = {
  meta: {
    currency: string;
    meet_and_greet: number;
    /** Euro figure per extra pickup (registry priceSource 'extra_pickup'). */
    extra_pickup_eur: number;
    /** Euro figure for a trailer on grid/p2p services (priceSource 'trailer'). */
    trailer_eur: number;
    bands: string[];
    pricing_basis: string;
  };
  airport_to_dublin: PricingRow[];
  golf: Record<string, PricingRow[]>;
};

// ---------------------------------------------------------------------------
// Parse + VALIDATE the price file (T608 M8 remediation)
// ---------------------------------------------------------------------------
/**
 * Parse docs/pricing.json and assert the shape everything downstream depends on.
 *
 * Both load sites used to bare-cast — `JSON.parse(raw) as PricingJson` in
 * src/bin/seed-pg.ts and src/bin/pricing-source.ts — which is a claim, not a check:
 * the cast makes the compiler treat any JSON at all as a valid price file. That
 * mattered the moment the seeder gained a DELETE. Measured through the shipped
 * `seedPg`: with `golf` edited to `{}` or `[]`, every seeded golf rate row was
 * removed and the seed COMMITTED with no error, while only a structurally ABSENT
 * `golf` key failed closed (by accident — `Object.entries(undefined)` throws in
 * `flattenPricing`). Since the Dockerfile CMD seeds on every container boot, the
 * difference between "throws" and "commits" is the difference between a failed
 * deploy and lost rate rows.
 *
 * It validates PRESENCE and non-emptiness of what the seeder and the snapshot
 * builder read, not price values — deliberately. Individual band cells are allowed
 * to be `null` (that is how an unpriced cell is expressed) and no price is bounded
 * here; this is a "the file is structurally a price list" gate, so it fails a
 * truncated, emptied or wrong-file input loudly and stays out of pricing policy.
 *
 * Pure and I/O-free like the rest of this module: it takes the raw text, so the
 * callers keep their own `readFileSync`.
 */
export function parsePricingJson(raw: string): PricingJson {
  const fail = (why: string): never => {
    throw new Error(`pricing.json is not a usable price file: ${why}`);
  };

  const parsed: unknown = JSON.parse(raw);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return fail('the top level is not a JSON object');
  }
  const p = parsed as Record<string, unknown>;

  const meta = p['meta'];
  if (typeof meta !== 'object' || meta === null || Array.isArray(meta)) {
    return fail('`meta` is missing or is not an object');
  }
  const m = meta as Record<string, unknown>;
  // Every one of these reaches app_meta and then the runtime quote path; a missing
  // add-on figure prices the add-on at 0 rather than failing (rate-snapshot's
  // '?? 0'), so absence has to be caught here, not discovered on a quote.
  for (const key of ['meet_and_greet', 'extra_pickup_eur', 'trailer_eur'] as const) {
    if (typeof m[key] !== 'number' || !Number.isFinite(m[key])) {
      return fail(`meta.${key} is not a finite number`);
    }
  }
  for (const key of ['currency', 'pricing_basis'] as const) {
    if (typeof m[key] !== 'string' || m[key] === '') {
      return fail(`meta.${key} is not a non-empty string`);
    }
  }
  if (!Array.isArray(m['bands']) || m['bands'].length === 0) {
    return fail('meta.bands is missing or empty');
  }

  if (!Array.isArray(p['airport_to_dublin']) || p['airport_to_dublin'].length === 0) {
    return fail('`airport_to_dublin` is missing or has no rows');
  }

  // `golf` is a hub -> routes MAP, so an array is a real (and silent) mistake:
  // Object.entries([]) is [] and would flatten to zero golf cells.
  const golf = p['golf'];
  if (typeof golf !== 'object' || golf === null || Array.isArray(golf)) {
    return fail('`golf` is missing or is not a hub-to-routes object');
  }
  const hubs = Object.entries(golf as Record<string, unknown>);
  if (hubs.length === 0) return fail('`golf` names no hubs');
  for (const [hub, routes] of hubs) {
    if (!Array.isArray(routes) || routes.length === 0) {
      return fail(`golf hub "${hub}" has no routes`);
    }
  }

  return parsed as PricingJson;
}

// ---------------------------------------------------------------------------
// A single priced (or null) rate cell
// ---------------------------------------------------------------------------
export type RateCell = {
  service: GridServiceId;
  /** hub slug for golf rows; empty string for airport */
  hub: string;
  locationId: string;
  /** Human-readable label for the location */
  locationLabel: string;
  band: Band;
  /** price in cents, or null = unpriced */
  priceCents: number | null;
};

// ---------------------------------------------------------------------------
// Slugify — single shared implementation
// ---------------------------------------------------------------------------
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ---------------------------------------------------------------------------
// Band → column mapping
// ---------------------------------------------------------------------------
const bandToCol: Record<Band, keyof PricingRow> = {
  '1-4': 'p1_4',
  '4-8': 'p4_8',
  '8-12': 'p8_12',
  '12-16': 'p12_16',
};

// ---------------------------------------------------------------------------
// Flatten result
// ---------------------------------------------------------------------------
export type FlattenResult = {
  cells: RateCell[];
  meta: {
    currency: string;
    meetAndGreetCents: number;
    extraPickupCents: number;
    trailerCents: number;
    bands: string[];
    pricingBasis: string;
  };
};

/**
 * Flatten a parsed pricing.json into rate cells + meta.
 * Pure function — no I/O, no side-effects.
 */
export function flattenPricing(pricing: PricingJson): FlattenResult {
  const cells: RateCell[] = [];

  // Airport rows
  for (const row of pricing.airport_to_dublin) {
    const locId = slugify(row.name);
    const label = row.name;
    for (const band of BANDS) {
      const col = bandToCol[band];
      const val = row[col] as number | null;
      cells.push({
        service: 'airport',
        hub: '',
        locationId: locId,
        locationLabel: label,
        band,
        priceCents: val !== null ? val * 100 : null,
      });
    }
  }

  // Golf rows: hub → routes
  for (const [hubName, routes] of Object.entries(pricing.golf)) {
    const hubSlug = slugify(hubName);
    for (const route of routes) {
      const routeSlug = slugify(route.name);
      const locId = `${hubSlug}__${routeSlug}`;
      // keep in sync with composeRouteLabel (shared/route-label.ts) — these bytes
      // ARE rates.location_label, so they are re-typed here rather than composed.
      const label = `${hubName} — ${route.name}`;
      for (const band of BANDS) {
        const col = bandToCol[band];
        const val = route[col] as number | null;
        cells.push({
          service: 'golf',
          hub: hubSlug,
          locationId: locId,
          locationLabel: label,
          band,
          priceCents: val !== null ? val * 100 : null,
        });
      }
    }
  }

  return {
    cells,
    meta: {
      currency: pricing.meta.currency,
      meetAndGreetCents: pricing.meta.meet_and_greet * 100,
      extraPickupCents: pricing.meta.extra_pickup_eur * 100,
      trailerCents: pricing.meta.trailer_eur * 100,
      bands: pricing.meta.bands,
      pricingBasis: pricing.meta.pricing_basis,
    },
  };
}
